/**
 * SegmentedControl — the app's 2-3-way single-select toggle (ClientOrdersPage
 * sort, Help Center audience; the AppSidebar workspace toggle is a themed
 * sidebar-token variant that should adopt this via a variant prop when next
 * touched). role="group" + aria-pressed, NOT Radix Tabs: callers filter one
 * shared view rather than swap panels.
 */
import { AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  /** Optional count chip after the label (e.g. "Pending 3"). Omit for none;
   *  `0` renders, so callers decide whether an empty bucket shows a zero. */
  badge?: number;
  /**
   * Optional full text for a label that may be long (an org name). When set,
   * the label truncates inside the segment and this text rides `title`, so
   * the control never grows sideways. Omit for today's rendering.
   */
  title?: string;
  /**
   * Optional "needs attention" marker: an amber (!) after the label, with this
   * text read to screen readers only (e.g. "2 sizes have no weight"). The
   * glyph and the words carry it, never the colour alone. Omit for none.
   */
  attention?: string;
  /**
   * Optional: this segment alone is disabled (native `disabled`); the caller
   * shows why in visible words beside the control. Omit for today's rendering.
   */
  disabled?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  size = 'sm',
  disabled = false,
  tone = 'default',
  busyValue,
}: {
  options: Array<SegmentedControlOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  size?: 'sm' | 'md';
  /** Disables every segment (native `disabled`); a click changes nothing. */
  disabled?: boolean;
  /**
   * `strong` fills the selected segment with the deep brand purple and white
   * text (the onboarding wizard's one-colour look). `default` is today's
   * white raised segment; every other caller keeps it.
   */
  tone?: 'default' | 'strong';
  /**
   * The segment whose change is saving: a spinner sits over its label (the
   * label stays in the accessible name, at zero opacity, so the segment keeps
   * its width) and it carries `aria-busy`. The control stays enabled (so
   * focus is kept); the caller's `onChange` ignores clicks while saving. Omit
   * for today's rendering.
   */
  busyValue?: T;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('inline-flex gap-0.5 rounded-md border bg-muted p-0.5', className)}
    >
      {options.map((option) => {
        const busy = busyValue !== undefined && option.value === busyValue;
        const off = disabled || option.disabled === true;
        const content = (
          <>
            {option.title !== undefined ? (
              <span className="block max-w-[14rem] truncate">{option.label}</span>
            ) : (
              option.label
            )}
            {option.attention !== undefined ? (
              <>
                <AlertCircle className="ml-1 inline-block h-3 w-3 align-[-2px] text-amber-600" aria-hidden="true" />
                <span className="sr-only">{`, ${option.attention}`}</span>
              </>
            ) : null}
            {option.badge !== undefined ? (
              <span
                className={cn(
                  'ml-1.5 inline-flex min-w-[1.15rem] justify-center rounded-full px-1 py-px text-[10px] font-semibold tabular-nums',
                  value === option.value
                    ? tone === 'strong'
                      ? 'bg-white/20 text-white'
                      : 'bg-foreground/10 text-foreground'
                    : 'bg-muted-foreground/15 text-muted-foreground',
                )}
              >
                {option.badge}
              </span>
            ) : null}
          </>
        );
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            aria-busy={busy || undefined}
            data-tone={tone}
            title={option.title}
            disabled={off}
            onClick={() => {
              if (!off) onChange(option.value);
            }}
            className={cn(
              'rounded-[5px] font-medium transition-colors duration-200 motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              size === 'sm' ? 'h-6 px-2.5 text-xs' : 'h-7 px-3 text-sm',
              value === option.value
                ? tone === 'strong'
                  ? 'bg-primary-strong text-white shadow-sm'
                  : 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
              busy && 'relative',
            )}
          >
            {busy ? (
              <>
                <span className="opacity-0">{content}</span>
                <Loader2
                  className="absolute inset-0 m-auto h-3 w-3 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              </>
            ) : (
              content
            )}
          </button>
        );
      })}
    </div>
  );
}
